/* RESPONSIBLE TEAM: team-automation-foundations */
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

// This service’s main purpose is to maintain the workflow title state in a shared location,
// preventing the title input field from losing focus.
//
// This seems an overkill. Why do we need this?
// The workflow title exists in several places:
// 1) Ruleset client data
// 2) Custom bot title
// 3) Workflow additional data
//
// As we introduce the draft workflow state feature, the title is rendered from the workflow’s additional data
// in both the graph editor and the header. Since both the header and the graph editor
// reactively depend on the workflow title, any update to the title triggers a re-render
// of both components. This re-render causes the title input to lose focus.
//
// To avoid this issue, we track the title state in this shared service,
// ensuring that the title input retains focus even when updates occur.

export default class WorkflowTitleEditorService extends Service {
  @tracked currentTitle = '';
  @tracked hasChanged = false;

  private initialValue = '';

  // Initialize the workflow title
  initialize(title = '') {
    this.currentTitle = title;
    this.initialValue = title;
    this.hasChanged = false;
  }

  updateTitle(value: string) {
    this.currentTitle = value;
    this.hasChanged = value !== this.initialValue;
  }

  resetHasChanged() {
    this.initialValue = this.currentTitle;
    this.hasChanged = false;
  }

  get isEmpty() {
    return this.currentTitle.trim() === '';
  }
}

declare module '@ember/service' {
  interface Registry {
    'workflow-title-editor': WorkflowTitleEditorService;
  }
}
